if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}

if (!customElements.get('product-shipping-calculator')) {
  customElements.define(
    'product-shipping-calculator',
    class ProductShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.querySelector('.form__message');
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleProductSelectionChange = this.handleProductSelectionChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('submit', this.handleFormSubmit);
        document.addEventListener('change', this.handleProductSelectionChange);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.handleFormSubmit);
        document.removeEventListener('change', this.handleProductSelectionChange);
      }

      get productForm() {
        return document.getElementById(this.dataset.productFormId);
      }

      handleProductSelectionChange(event) {
        if (event.target.form !== this.productForm) return;
        if (!['id', 'quantity'].includes(event.target.name)) return;

        this.resultsElement.hidden = true;
        this.resultsElement.replaceChildren();
      }

      async handleFormSubmit(event) {
        event.preventDefault();

        if (!this.checkValidity()) {
          this.reportValidity();
          return;
        }

        const variantId = Number.parseInt(this.productForm?.querySelector('[name="id"]')?.value, 10);
        const quantityInput = this.productForm?.elements.namedItem('quantity');
        const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10));
        const zip = this.querySelector('[name="address[zip]"]').value.replace(/\D/g, '');

        if (!variantId) {
          this.formatError({ variant: FoxTheme.shippingCalculatorStrings.notFound });
          this.resultsElement.hidden = false;
          return;
        }

        this.submitButton.disabled = true;
        this.submitButton.classList.add('btn--loading');
        this.resultsElement.hidden = true;

        let cartRestoreState;

        try {
          const postalCodeData = await this.fetchPostalCode(zip);
          cartRestoreState = await this.addProductTemporarily(variantId, quantity);
          const shippingRates = await this.fetchShippingRates(zip, postalCodeData.state);
          this.formatShippingRates(shippingRates);
        } catch (error) {
          console.error(error);
          this.formatError({ zip: FoxTheme.shippingCalculatorStrings.notFound });
        } finally {
          if (cartRestoreState) {
            try {
              await this.restoreCart(cartRestoreState);
            } catch (error) {
              console.error(error);
            }
          }

          this.resultsElement.hidden = false;
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('btn--loading');
        }
      }

      async fetchPostalCode(zip) {
        const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${encodeURIComponent(zip)}`);

        if (!response.ok) {
          throw new Error(`BrazilAPI request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.state) {
          throw new Error('BrazilAPI response did not include a state');
        }

        return data;
      }

      async addProductTemporarily(variantId, quantity) {
        const cart = await this.fetchJson(
          FoxTheme.routes.cart_url,
          FoxTheme.utils.fetchConfig('json', 'GET')
        );
        const added = await this.fetchJson(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });
        const addedItem = added.items?.[0] || added;

        if (!addedItem.key) {
          throw new Error('Shopify did not return the temporary cart item key');
        }

        const originalItem = cart.items.find((item) => item.key === addedItem.key);

        return {
          key: addedItem.key,
          quantity: originalItem?.quantity || 0,
        };
      }

      async restoreCart({ key, quantity }) {
        let lastError;

        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            await this.fetchJson(FoxTheme.routes.cart_update_url, {
              ...FoxTheme.utils.fetchConfig(),
              body: JSON.stringify({ updates: { [key]: quantity } }),
            });
            return;
          } catch (error) {
            lastError = error;
          }
        }

        throw lastError;
      }

      async fetchShippingRates(zip, province) {
        let url = `${FoxTheme.routes.cart_url}/shipping_rates.json`;
        url = url.replace('//', '/');

        const data = await this.fetchJson(url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify({
            shipping_address: { zip, country: 'Brazil', province },
          }),
        });

        if (!data.shipping_rates) {
          throw new Error('Shopify did not return shipping rates');
        }

        return data.shipping_rates;
      }

      async fetchJson(url, options) {
        const response = await fetch(url, options);
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || `Request failed with status ${response.status}`);
        }

        return data;
      }

      formatError(errors) {
        const messages = Object.values(errors || {}).flatMap((value) => (Array.isArray(value) ? value : [value]));
        this.renderMessage('error', FoxTheme.shippingCalculatorStrings.error, messages);
      }

      formatShippingRates(shippingRates) {
        const rates = shippingRates.map(({ presentment_name: name, price }) => {
          const formattedPrice = FoxTheme.Currency.formatMoney(
            Math.round(Number.parseFloat(price) * 100),
            FoxTheme.settings.moneyFormat
          );

          return `${name}: ${formattedPrice}`;
        });
        const title =
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults;

        this.renderMessage(shippingRates.length === 0 ? 'error' : 'success', title, rates);
      }

      renderMessage(type, title, messages = []) {
        const alert = document.createElement('div');
        const heading = document.createElement('p');

        alert.className = `alert blocks-radius alert--${type} grid gap-2 leading-tight`;
        heading.className = 'font-body-bolder m-0';
        heading.textContent = title;
        alert.append(heading);

        if (messages.length > 0) {
          const list = document.createElement('ul');
          list.className = 'list-disc grid gap-1 text-sm';
          list.setAttribute('role', 'list');

          messages.forEach((message) => {
            const item = document.createElement('li');
            item.textContent = message;
            list.append(item);
          });

          alert.append(list);
        }

        this.resultsElement.replaceChildren(alert);
      }
    },
    { extends: 'form' }
  );
}
